/**
 * Per-device settings, kept in localStorage.
 *
 * The passphrase lives here and nowhere else — never in the repo, never in a
 * URL, never sent to the Worker. That is the whole basis of the zero-knowledge
 * claim: the host stores bytes it has no key for, so "can the host read my work
 * usage" has a structural answer rather than a policy one.
 *
 * A capability URL (token in the query string) was the alternative and is worse:
 * it leaks into browser history, into the address bar, and into any link you
 * paste. One setup form per device is a small price for not doing that.
 */

const KEY = "statsai.settings.v1";

export interface Settings {
  /** Dead-drop Worker base URL. */
  dropUrl: string;
  /** Bearer token the Worker requires to read. */
  readToken: string;
  /** Decrypts the blobs. Never leaves this browser. */
  passphrase: string;
}

const EMPTY: Settings = { dropUrl: "", readToken: "", passphrase: "" };

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      dropUrl: typeof parsed.dropUrl === "string" ? parsed.dropUrl : "",
      readToken: typeof parsed.readToken === "string" ? parsed.readToken : "",
      passphrase: typeof parsed.passphrase === "string" ? parsed.passphrase : "",
    };
  } catch {
    // Corrupt or unavailable storage (private mode) — start clean rather than
    // crashing the page before it can show the settings form.
    return { ...EMPTY };
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Storage unavailable: the session still works, it just won't persist.
  }
}

export function clearSettings(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do — the caller reloads either way.
  }
}

/** Whether there's enough to attempt a load. Validity is proven by trying. */
export function isConfigured(s: Settings): boolean {
  return s.dropUrl.trim() !== "" && s.readToken.trim() !== "" && s.passphrase !== "";
}
